import { cn, formatNum } from '@/lib/utils'
import type { ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

export function StatStrip({
  items,
}: {
  items: { label: string; value: string | number; hint?: string }[]
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {items.map((item, i) => (
        <div
          key={item.label}
          className="panel relative overflow-hidden px-4 py-4"
          style={{ animationDelay: `${i * 40}ms` }}
        >
          <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-line-strong" />
          <div className="text-[11px] uppercase tracking-[0.14em] text-ink-faint">{item.label}</div>
          <div className="stat-value mt-2">
            {typeof item.value === 'number' ? formatNum(item.value) : item.value}
          </div>
          {item.hint ? <div className="mt-1 text-xs text-ink-faint">{item.hint}</div> : null}
        </div>
      ))}
    </div>
  )
}

export function Panel({
  title,
  action,
  children,
  className,
  flush,
}: {
  title?: string
  action?: ReactNode
  children: ReactNode
  className?: string
  flush?: boolean
}) {
  return (
    <section className={cn('panel overflow-hidden', className)}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          {title ? (
            <h2 className="section-title flex items-center gap-2">
              <span className="h-1 w-1 rounded-full bg-accent" />
              {title}
            </h2>
          ) : (
            <span />
          )}
          {action}
        </div>
      )}
      <div className={flush ? undefined : 'p-4'}>{children}</div>
    </section>
  )
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="mb-3 h-px w-12 bg-line" />
      <p className="text-sm text-ink-faint">{message}</p>
    </div>
  )
}

export function LoadingBlock({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-sm text-ink-muted">
      <Loader2 className="h-5 w-5 animate-spin text-accent" />
      <span className="tracking-wide">{label}</span>
    </div>
  )
}

export function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="rounded-panel border border-red-500/30 bg-red-950/30 px-4 py-3 text-sm text-red-300">
      {message}
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  action,
  breadcrumb,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
  breadcrumb?: ReactNode
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
      <div>
        {breadcrumb ? <div className="mb-2">{breadcrumb}</div> : null}
        <h1 className="page-title">{title}</h1>
        {subtitle ? <p className="page-subtitle">{subtitle}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}
